import { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react';
import WebApp from '@twa-dev/sdk';
import type { WebAppUser, WebApp as WebAppType } from '@twa-dev/types';

interface ITelegramContext {
  user?: WebAppUser;
  webApp?: WebAppType;
  initData?: string;
  isReady: boolean;
  error?: Error;
}

const TelegramContext = createContext<ITelegramContext>({ isReady: false });

// Этот компонент будет содержать логику инициализации SDK
const TelegramInnerProvider = ({ children }: { children: ReactNode }) => {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    try {
      // WebApp.ready() синхронно помечает SDK как готовый или кидает ошибку
      WebApp.ready();
      
      // 🔧 CRITICAL: Expand to full screen height (Telegram Mini App Best Practice)
      // This maximizes the available viewport on mobile devices
      WebApp.expand();
      
      // 🎨 Set header color to match app theme
      WebApp.setHeaderColor('#ffffff');
      
      // 🔍 DEBUG: Log raw initData for debugging
      console.log('=== 🔍 TELEGRAM initData DEBUG ===');
      console.log('initData:', WebApp.initData);
      console.log('initData length:', WebApp.initData?.length);
      console.log('Has escape sequences:', WebApp.initData?.includes('%5C%2F') || WebApp.initData?.includes('\\/'));
      console.log('viewport height:', WebApp.viewportHeight);
      console.log('viewport stable height:', WebApp.viewportStableHeight);
      console.log('is expanded:', WebApp.isExpanded);
      
      // Parse and log hash
      if (WebApp.initData) {
        const urlParams = new URLSearchParams(WebApp.initData);
        console.log('hash:', urlParams.get('hash'));
        console.log('signature:', urlParams.get('signature'));
        console.log('auth_date:', urlParams.get('auth_date'));
        console.log('user (first 100 chars):', urlParams.get('user')?.substring(0, 100));
      }
      console.log('=== END DEBUG ===');
      
      setIsReady(true);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to initialize WebApp'));
    }
  }, []);

  const value = useMemo(() => ({
    webApp: WebApp,
    user: WebApp.initDataUnsafe?.user,
    initData: WebApp.initData,
    isReady,
    error,
  }), [isReady, error]);

  return (
    <TelegramContext.Provider value={value}>
      {children}
    </TelegramContext.Provider>
  );
}

// Экспортируем основной провайдер
export const TelegramProvider = ({ children }: { children: ReactNode }) => {
  return (
    <TelegramInnerProvider>
      {children}
    </TelegramInnerProvider>
  );
};

export const useTelegram = () => useContext(TelegramContext);
